import React, { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import {
  Link,
  LinkProps,
  useLocation,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import { LogOut } from 'lucide-react'

import { useAuth } from '@/services/auth/use-auth'
import { queryUserInformation } from '@/services/user/query'

import { Button } from '@/components/ui/button'

import { getFallbackName } from '@/lib/utils/utils'

import { cn } from '@/lib/cn'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Container } from './container'

interface NavItem {
  label: string
  to: LinkProps['to']
}

const NAV_AUTH: NavItem[] = []

const NAV_USER: NavItem[] = [
  { label: 'DASHBOARD', to: '/dashboard' },
  { label: 'PROFILE', to: '/profile' },
]

interface SiteHeaderProps {
  typeNav: 'auth' | 'user'
  action?: React.ReactNode
}

export function SiteHeader(props: SiteHeaderProps) {
  const { typeNav, action } = props

  const navItems = useMemo(() => {
    return typeNav === 'auth' ? NAV_AUTH : NAV_USER
  }, [typeNav])

  const auth = useAuth.getState()
  const isAdmin = auth.isAdmin()

  return (
    <div className="sticky top-0 z-40 flex min-h-16 w-screen items-center border-b border-gray-300 bg-white">
      <Container className="flex w-full flex-wrap items-center gap-2 py-2">
        {!isAdmin ? (
          <>
            <Logo />
            <div className="flex-1">
              <HeaderNavList items={navItems} />
            </div>
          </>
        ) : (
          <>
            <div className="flex-1"></div>
          </>
        )}

        {action || <SiteHeaderActionAutoMap />}
      </Container>
    </div>
  )
}

function SiteHeaderActionAutoMap() {
  const user = useAuth((s) => s.user)
  return user?.id ? (
    <SiteHeaderActionUser
      user={{
        name: user.name,
        email: user.email,
      }}
    />
  ) : (
    <SiteHeaderActionAuth />
  )
}

export function SiteHeaderActionAuth() {
  return (
    <div className="flex flex-1 justify-end gap-1">
      <Button className="rounded-full border-black" variant="outline" asChild>
        <Link to="/auth/login">Masuk</Link>
      </Button>
      <Button
        className="rounded-full bg-gradient-to-r from-[#D91F2E] to-[#AE000E]"
        asChild
      >
        <Link to="/auth/register">Daftar</Link>
      </Button>
    </div>
  )
}

interface SiteHeaderActionUserProps {
  user: {
    name: string
    email: string
    avatar?: string
  }
}

export function SiteHeaderActionUser(props: SiteHeaderActionUserProps) {
  const { user } = props
  const router = useRouter()
  const navigate = useNavigate()
  const logout = useAuth((s) => s.logout)

  const { data: userPersonalInfo } = useQuery(queryUserInformation())
  const fallbackName = useMemo(
    () => getFallbackName(user?.name || userPersonalInfo?.name || user.email),
    [user]
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarImage
              src={userPersonalInfo ? userPersonalInfo?.photo : '#'}
              alt="Avatar"
            />
            <AvatarFallback className="bg-transparent">
              {fallbackName}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.name}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* <DropdownMenuGroup>
          <DropdownMenuItem className="hover:cursor-pointer" asChild>
            <Link to="/" className="flex items-center">
              <LayoutGrid className="mr-3 h-4 w-4 text-muted-foreground" />
              Dashboard
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="hover:cursor-pointer" asChild>
            <Link to="/" className="flex items-center">
              <User className="mr-3 h-4 w-4 text-muted-foreground" />
              Account
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup> */}
        {/* <DropdownMenuSeparator /> */}
        <DropdownMenuItem
          className="hover:cursor-pointer"
          onClick={() => {
            logout().then(() => {
              navigate({ to: '/auth/login' })
              router.invalidate()
            })
          }}
        >
          <LogOut className="mr-3 h-4 w-4 text-muted-foreground" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function HeaderNavList(props: { items: NavItem[] }) {
  const { pathname } = useLocation()
  return (
    <ul className="flex gap-4">
      {props.items.map((item, index) => {
        const isActive = pathname.startsWith(item.to as string)
        return (
          <li key={index}>
            <Link
              to={item.to}
              className={cn(
                'text-sm transition-colors duration-300 hover:text-primary-yes/50 md:text-base',
                isActive && 'text-primary-yes'
              )}
            >
              {item.label}
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

export function Logo() {
  return (
    <Link to="/" className="mr-1 md:mr-4">
      <img src="/YES-logo.png" alt="logo" className="h-7 md:h-9" />
    </Link>
  )
}
